import { z } from 'zod';
import { ERROR_MESSAGES } from '~/constants';
import { BaseFormContext } from '~/machines/base-crud';

/** Local-timezone `YYYY-MM-DD` for safely comparing against `z.iso.date`
 *  strings. `new Date()` is local-wall-clock-as-UTC-instant while
 *  `new Date('2026-04-17')` is UTC midnight — comparing the two flips for
 *  timezone-offset users near day boundaries. Lexicographic string compare
 *  on matching YYYY-MM-DD forms sidesteps the problem entirely. */
const todayIso = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

export type InsuranceSortOptions =
  | 'insurance_company'
  | 'policy_number'
  | 'effective_date'
  | 'active_flag'
  | 'id';

export type InsuranceSort = {
  sortBy: InsuranceSortOptions;
  insurance_company: number;
  policy_number: number;
  effective_date: number;
  active_flag: number;
  id: number;
  page: number;
  pageSize: number;
};

export type InsuranceContext = BaseFormContext<MalpracticeInsuranceFormValues, InsuranceSort>;

export const malpracticeInsuranceSchema = z
  .object({
    activeFlag: z.boolean(),
    coverageLimits: z.string().max(256, ERROR_MESSAGES.MAX_CHARACTER_LIMIT(256)),
    effectiveDate: z.iso.date(ERROR_MESSAGES.INVALID_DATE),
    expiryDate: z.iso.date(ERROR_MESSAGES.INVALID_DATE),
    id: z.number().optional(),
    insuranceCompany: z
      .string()
      .min(1, ERROR_MESSAGES.REQUIRED)
      .max(64, ERROR_MESSAGES.MAX_CHARACTER_LIMIT(64)),
    policyNumber: z.string().max(64, ERROR_MESSAGES.MAX_CHARACTER_LIMIT(64)),
    policyType: z.object({
      value: z.string().max(32, ERROR_MESSAGES.MAX_CHARACTER_LIMIT(32)).readonly(),
      label: z.string().readonly(),
    }),
    producer: z.string().max(64, ERROR_MESSAGES.MAX_CHARACTER_LIMIT(64)),
    validFlag: z.boolean().optional(),
    warnings: z.record(z.string(), z.string()).optional(),
  })
  // [MCV-1251] Skip the ordering refine when either date is still empty so a
  // user who has filled only one picker doesn't see the long
  // "Start Date must be before End Date" fire on an otherwise-valid field —
  // that error is longer than the sibling's "Invalid date" and misaligned the
  // two pickers by ~8px in the shared row. Matches the guard pattern in
  // educationSchema / trainingSchema.
  .refine(
    (data) => !data.effectiveDate || !data.expiryDate || data.effectiveDate < data.expiryDate,
    {
      message: ERROR_MESSAGES.INVALID_START_END_DATES,
      path: ['effectiveDate'],
    }
  )
  // [MCV-1251] active/inactive refines also swallow an empty expiryDate
  // through `new Date('')` (Invalid Date), producing misleading errors.
  // Gate on a non-empty expiryDate so `z.iso.date`'s own "Invalid date"
  // message is the only thing that surfaces while the field is untouched.
  // Date comparison uses local-tz YYYY-MM-DD strings (see `todayIso`) so a
  // policy expiring today isn't flipped "past" at night for timezone-offset
  // users.
  .refine(
    (data) => {
      if (!data.activeFlag || !data.expiryDate) return true;
      return data.expiryDate >= todayIso();
    },
    {
      message: 'Expiration date must be in the future if policy is active',
      path: ['expiryDate'],
    }
  )
  .refine(
    (data) => {
      if (data.activeFlag || !data.expiryDate) return true;
      return data.expiryDate < todayIso();
    },
    {
      message: 'Expiration date must be in the past if policy is inactive',
      path: ['expiryDate'],
    }
  );

export type MalpracticeInsuranceFormValues = z.infer<typeof malpracticeInsuranceSchema>;
export type CreateMalpracticeInsuranceFormValues = Omit<MalpracticeInsuranceFormValues, 'id'>;
